import React from "react";
import * as d3 from "d3";
import { formatNumber } from "util/utils";
import { PaymentPlanDto } from "util/DTOs/PaymentPlanDtos";
import { ContainerElement } from "d3";
import ReactDOM from "react-dom";

export interface PaymentPlanData {
    currentDateStr: string;
    currentBalance: number;
    paymentAmount: number;

    originalBalance: number;
    firstDisbursementDateStr?: string;

    futurePlan: PaymentPlanDto[];
}

export interface IPaymentPlanPlotProps {
    height: number;
    width: number;
    paymentPlanData: PaymentPlanData;
    chartMax?: number;
    chartMin?: number;
    loanId: number;
}

interface IPaymentPlanPlotState {
    chartWidth: number;
    beginDate: Date;
    domainEndDate: Date;

    paymentPlanIndex: number;
}

const HYPOBLUE = "#004170";
const HYPOLBLUE = "#2196F3";
const HYPOGREEN = "#63A51D";
const marginTop = 35;
const marginLeft = 10;

export class PaymentPlanPlot extends React.Component<
    IPaymentPlanPlotProps,
    IPaymentPlanPlotState
> {
    node: SVGSVGElement | null = null;
    rootView: d3.Selection<SVGSVGElement | null, {}, null, undefined>;

    constructor(props: IPaymentPlanPlotProps) {
        super(props);

        this.state = {
            paymentPlanIndex: 0,
            chartWidth: 0,
            beginDate: new Date(),
            domainEndDate: new Date(),
        };
    }

    componentDidMount() {
        this.drawPaymentPlanPlot();
    }

    componentWillUpdate() {
        this.drawPaymentPlanPlot();
    }

    componentWillReceiveProps() {
        this.drawPaymentPlanPlot();
    }

    plotTap = () => {
        const mouse = d3.mouse(this.node as ContainerElement);
        const containerView = document.getElementsByClassName(
            "react-swipeable-view-container"
        )[0];
        const containerWidth = containerView.clientWidth;

        // Subtract left margin
        let mouseX = mouse[0] - marginLeft;

        // Hack to make mouse position work on FF with react-swipeable-views
        if (mouseX < 0) {
            const x = mouseX * -1;
            mouseX = mouseX + (~~(x / containerWidth) + 1) * containerWidth;
        }

        const xScale = this.getXScale(
            this.state.chartWidth,
            this.state.beginDate,
            this.state.domainEndDate
        );
        const data = xScale.invert(mouseX);

        let futurePlan = this.props.paymentPlanData.futurePlan;
        const index = futurePlan.findIndex(
            (item) => new Date(item.dueDate) >= data
        );

        if (index === -1) {
            return;
        }

        this.setState(
            {
                ...this.state,
                paymentPlanIndex: index,
            },
            () => {
                this.drawPaymentPlanPlot();
            }
        );
    };

    plotDrag = () => {
        const mouse = d3.mouse(this.node as ContainerElement);
        const containerView = document.getElementsByClassName(
            "react-swipeable-view-container"
        )[0];
        const containerWidth = containerView.clientWidth;

        // Subtract left margin
        let mouseX = mouse[0] - marginLeft;

        // Hack to make mouse position work on FF with react-swipeable-views
        if (mouseX < 0) {
            const x = mouseX * -1;
            mouseX = mouseX + (~~(x / containerWidth) + 1) * containerWidth;
        }

        const xScale = this.getXScale(
            this.state.chartWidth,
            this.state.beginDate,
            this.state.domainEndDate
        );
        const data = xScale.invert(mouseX);

        let futurePlan = this.props.paymentPlanData.futurePlan;
        const index = futurePlan.findIndex(
            (item) => new Date(item.dueDate) >= data
        );

        if (index === -1) {
            return;
        }

        this.setState(
            {
                ...this.state,
                paymentPlanIndex: index,
            },
            () => {
                this.drawPaymentPlanPlot();
            }
        );
    };

    getXScale = (chartWidth: number, beginDate: Date, domainEndDate: Date) => {
        return d3
            .scaleTime()
            .range([0, chartWidth])
            .domain([beginDate, domainEndDate]);
    };

    getYScale = (chartHeight: number, chartMin: number, chartMax: number) => {
        return d3
            .scaleLinear()
            .range([chartHeight, 0])
            .domain([chartMin + 1, chartMax + (chartMax - chartMin) / 3]);
    };

    setVariables = () => {
        const futurePlan = this.props.paymentPlanData.futurePlan;

        const svgWidth = this.props.width;

        const chartWidth = svgWidth > 300 ? svgWidth - 40 : svgWidth - 50;

        const beginDate = new Date(
            this.props.paymentPlanData.firstDisbursementDateStr || ""
        );
        const endDate = new Date(futurePlan[futurePlan.length - 1].dueDate);

        const domainEndDate = new Date(
            endDate.getFullYear() + 5,
            endDate.getMonth(),
            endDate.getDate()
        );

        this.setState({
            chartWidth,
            beginDate,
            domainEndDate,
        });
    };

    drawPaymentPlanPlot = () => {
        this.rootView && this.rootView.selectAll("*").remove();
        this.rootView = d3.select(this.node);

        const futurePlan = this.props.paymentPlanData.futurePlan;

        let plotData = futurePlan.map((item, key) => {
            return {
                time: new Date(item.dueDate),
                value: item.remainingAmount,
            };
        });

        const chartMax =
            this.props.chartMax ||
            this.props.paymentPlanData.originalBalance + 1000;
        const chartMin = 0;

        const svgWidth = this.props.width;
        const svgHeight = this.props.height;

        const chartWidth = svgWidth > 300 ? svgWidth - 40 : svgWidth - 50;
        const chartHeight = svgHeight - 85;

        const beginDate = new Date(
            this.props.paymentPlanData.firstDisbursementDateStr || ""
        );
        const endDate = new Date(futurePlan[futurePlan.length - 1].dueDate);

        const domainEndDate = new Date(
            endDate.getFullYear() + 5,
            endDate.getMonth(),
            endDate.getDate()
        );
        const currentDate = new Date(this.props.paymentPlanData.currentDateStr);

        if (this.state === null || !(this.state.chartWidth > 0)) {
            this.setVariables();
        }

        const currentIndex = this.state.paymentPlanIndex;
        const indexedDate = new Date(futurePlan[currentIndex].dueDate);
        const indexedPlanData = futurePlan[currentIndex];

        let svg = d3
            .select(this.node)
            .data([plotData])
            .attr("width", svgWidth)
            .attr("height", svgHeight)
            .append("g")
            .attr("transform", `translate(${marginLeft}, ${marginTop})`);

        let x = this.getXScale(chartWidth, beginDate, domainEndDate);

        let y = this.getYScale(chartHeight, chartMin, chartMax);
        let currentY = y(indexedPlanData.remainingAmount);

        let planLine = d3
            .line()
            .x((d: any) => {
                return x(d.time);
            })
            .y((d: any) => {
                return y(d.value);
            });

        let historyLine = d3
            .line()
            .x((d: any) => {
                return x(d.time);
            })
            .y((d: any) => {
                return y(d.value);
            });

        let area = d3
            .area()
            .x(function (d: any) {
                return x(d.time);
            })
            .y0(chartHeight)
            .y1(function (d: any) {
                return y(d.value);
            });

        // Filter def
        d3.select(this.node).append("defs");

        /**
         * Define arrow end marker
         */
        d3.select("defs")
            .append("marker")
            .attr("id", "arrowhead")
            .attr("viewBox", "0 0 10 10")
            .attr("refX", 5.5)
            .attr("refY", 5)
            .attr("markerWidth", 4)
            .attr("markerHeight", 4)
            .attr("orient", "auto")
            .attr("fill", HYPOBLUE)
            .append("path")
            .attr("d", "M0,0 L2,0 L5,6 L8,0 L10,0 L5,10")
            .attr("class", "arrowHead");

        d3.select("defs")
            .append("marker")
            .attr("id", "eol-dot")
            .attr("refX", 5)
            .attr("refY", 5)
            .attr("markerWidth", 8)
            .attr("markerHeight", 8)
            .attr("fill", HYPOBLUE)
            .attr("stroke-dasharray", "100")
            .append("circle")
            .attr("r", "1.5")
            .attr("cx", 5)
            .attr("cy", 5)
            .attr("class", "circle-filled");

        d3.select("defs")
            .append("marker")
            .attr("id", "eol-circle")
            .attr("refX", 6)
            .attr("refY", 6)
            .attr("markerWidth", 14)
            .attr("markerHeight", 14)
            .attr("fill", HYPOBLUE)
            .attr("stroke-dasharray", "100")
            .append("circle")
            .attr("r", "5")
            .attr("cx", 6)
            .attr("cy", 6)
            .attr("class", "circle");

        // add the area
        const beg: { time: Date; value: number } = {
            time: beginDate,
            value: this.props.paymentPlanData.originalBalance,
        };
        let plotData2: { time: Date; value: number }[] = [beg];
        plotData2.push(...plotData);

        svg.append("path")
            .data([plotData2])
            .attr("class", "area")
            .attr("d", area as any);

        /**
         * Path from current moment to the end of payment plan
         */
        svg.append("path")
            .datum(plotData)
            .attr("fill", "none")
            .attr("stroke-linejoin", "round")
            .attr("stroke-dasharray", "5, 5")
            .attr("d", planLine as any);

        /**
         * Vertical dotted line to indicate end of plan
         * in the plot
         */
        svg.append("line")
            .attr("class", "estimation-line")
            .attr("x1", x(endDate))
            .attr("y1", chartHeight + 15)
            .attr("x2", x(endDate))
            .attr(
                "y2",
                currentY > chartHeight - 47
                    ? chartHeight - 65
                    : chartHeight - 30
            )
            .attr("stroke-dasharray", "5, 5")
            .style("fill", "none")
            .attr("marker-end", "url(#eol-dot)");

        /**
         * Solid grey line between begin of payments to the current situation
         */
        svg.append("path")
            .attr("class", "dashed")
            .datum([
                {
                    time: beginDate,
                    value: this.props.paymentPlanData.originalBalance,
                },
                {
                    time: currentDate,
                    value: this.props.paymentPlanData.currentBalance,
                },
            ])
            .attr("fill", "none")
            .attr("stroke-linejoin", "round")
            .attr("d", historyLine as any);

        /**
         * Horizontal dotted line to indicate original amount of the loan
         */
        svg.append("line")
            .attr("class", "estimation-line")
            .attr("x1", -10)
            .attr("y1", y(this.props.paymentPlanData.originalBalance))
            .attr("x2", 30)
            .attr("y2", y(this.props.paymentPlanData.originalBalance))
            .attr("stroke-dasharray", "5, 5")
            .style("fill", "none")
            .attr("marker-end", "url(#eol-dot)");

        /**
         * Text for the original loan amount
         */
        let bgRectOriginalBalance = svg.append("rect"); //Just insert rect before text to get the text on top of it

        let elemOriginalBalance = svg
            .append("text")
            .attr("class", "amount-label")
            .attr("x", 5)
            .attr("y", y(this.props.paymentPlanData.originalBalance) - 9)
            .text(
                formatNumber(this.props.paymentPlanData.originalBalance, 0) +
                    " €"
            );

        if (currentY - 25 < y(this.props.paymentPlanData.originalBalance)) {
            currentY = currentY + 9;
        }

        let txtOriginalBalance = elemOriginalBalance.node() as SVGTextElement;
        const bboxOriginalBalance = txtOriginalBalance.getBBox();

        /**
         * End date text.
         */
        let bgRectEndDate = svg.append("rect"); //Just insert rect before text to get the text on top of it
        let elemEndDate = svg
            .append("text")
            .attr("class", "hypolblue-text")
            .attr("x", x(endDate) - 35)
            .attr(
                "y",
                currentY > chartHeight - 47
                    ? chartHeight - 72
                    : chartHeight - 37
            )
            .text(
                `${endDate.getDate()}.${
                    endDate.getMonth() + 1
                }.${endDate.getFullYear()}`
            );

        let txtEndDate = elemEndDate.node() as SVGTextElement;
        const bboxEndDate = txtEndDate.getBBox();

        /**
         * End date text background
         */
        bgRectEndDate
            .attr("fill", "#FFF")
            .attr("x", bboxEndDate.x)
            .attr("y", bboxEndDate.y)
            .attr("width", bboxEndDate.width)
            .attr("height", bboxEndDate.height);

        /**
         * Label text of the current amount
         */
        let bgRectIndexed = svg.append("rect");
        let elemIndexed = svg
            .append("text")
            .attr("class", "current-amount-label")
            .attr("x", x(indexedDate) + 10)
            .attr("y", currentY - 5)
            .text(formatNumber(indexedPlanData.remainingAmount, 2) + " €");

        let txtIndexed = elemIndexed.node() as SVGTextElement;
        let bboxIndexed = txtIndexed.getBBox();

        if (bboxIndexed.x + bboxIndexed.width - marginLeft > chartWidth) {
            elemIndexed.attr("x", x(indexedDate) - bboxIndexed.width - 8);
        } else {
            /**
             * Indexed text background
             */
            bgRectIndexed
                .attr("fill", "#FFF")
                .attr("x", bboxIndexed.x)
                .attr("y", bboxIndexed.y)
                .attr("width", bboxIndexed.width)
                .attr("height", bboxIndexed.height);
        }

        /**
         * Payment amount and label
         */
        let amortLabel = svg
            .append("text")
            .attr("class", "hypogrey-text")
            .attr("x", x(domainEndDate) - 140)
            .attr("y", -10)
            .attr("font-size", 12)
            .text("Lyhennys");

        let amortAmount = svg
            .append("text")
            .attr("class", "hypoblue-text payment")
            .attr("x", x(domainEndDate) - 140)
            .attr("y", 5)
            .text(
                (formatNumber(indexedPlanData.instalmentAmount) || "-") + " €"
            );

        let interestLabel = svg
            .append("text")
            .attr("class", "hypogrey-text")
            .attr("x", x(domainEndDate) - 55)
            .attr("y", -10)
            .attr("font-size", 12)
            .text("Korot");

        let interestAmount = svg
            .append("text")
            .attr("class", "hypoblue-text payment")
            .attr("x", x(domainEndDate) - 55)
            .attr("y", 5)
            .text((formatNumber(indexedPlanData.interestAmount) || "-") + " €");

        let txtAmort = amortAmount.node() as SVGTextElement;
        const abbox = txtAmort.getBBox();
        let bboxAmort: {
            x: number;
            y: number;
            width: number;
            height: number;
        } = { x: abbox.x, y: abbox.y, width: abbox.width, height: abbox.width };

        let txtInterest = interestAmount.node() as SVGTextElement;
        const ibbox = txtInterest.getBBox();
        let bboxInterest: {
            x: number;
            y: number;
            width: number;
            height: number;
        } = { x: ibbox.x, y: ibbox.y, width: ibbox.width, height: ibbox.width };

        if (bboxInterest.x + bboxInterest.width >= chartWidth) {
            const offset = bboxInterest.x + bboxInterest.width - chartWidth;
            interestAmount.attr("x", bboxInterest.x - offset);
            interestLabel.attr("x", bboxInterest.x - offset);

            bboxInterest.x = bboxInterest.x - offset;
            bboxAmort.x = bboxAmort.x - offset;
        }

        if (bboxAmort.x + bboxAmort.width >= bboxInterest.x - 10) {
            const offset =
                bboxAmort.x + bboxAmort.width - (bboxInterest.x - 10);
            amortAmount.attr("x", bboxAmort.x - offset);
            amortLabel.attr("x", bboxAmort.x - offset);
        }

        // Y-axis "label"
        svg.append("text")
            .attr("class", "hypoblue-text")
            .attr("x", -10)
            .attr("y", -20)
            .text("Lainan");
        svg.append("text")
            .attr("class", "hypoblue-text")
            .attr("x", -10)
            .attr("y", -5)
            .text("saldo");

        /**
         * Current amount and date texts.
         */
        svg.append("text")
            .attr("class", "hypoblue-text")
            .attr("x", () => {
                return currentIndex === 0
                    ? x(indexedDate) - 35
                    : x(indexedDate) - 25;
            })
            .attr("y", chartHeight + 32)
            .text(currentIndex === 0 ? "Nykyhetki" : "Eräpvm.");

        svg.append("text")
            .attr("class", "amount-label")
            .attr("x", x(indexedDate) - 30)
            .attr("y", chartHeight + 47)
            .text(
                `${indexedDate.getDate()}.${
                    indexedDate.getMonth() + 1
                }.${indexedDate.getFullYear()}`
            );

        /**
         * X-axis with arrow end marker
         */
        svg.append("g")
            .attr("transform", `translate(0, ${chartHeight})`)
            .call(d3.axisBottom(x).ticks(0).tickSize(0))
            .attr("class", "x")
            .attr("marker-end", "url(#arrowhead)")
            .selectAll(".tick text")
            .attr("y", "10");

        /**
         * Y-axis with arrow end marker
         */
        svg.append("g")
            .call(d3.axisLeft(y).ticks(0).tickSize(0))
            .attr("transform", `translate(0, 0)`)
            .attr("class", "y")
            .attr("marker-end", "url(#arrowhead)")
            .selectAll(".tick text")
            .attr("x", "-10");

        /**
         * Re-color the axis lines
         */
        svg.selectAll(".domain")
            .attr("stroke", HYPOBLUE)
            .attr("stroke-width", "2px");

        /**
         * Vertical dotted line to indicate current date and amount
         * in the plot
         */
        let drag = d3.drag().on("drag", this.plotDrag);

        let bgRectDraggable = svg
            .append("rect")
            .attr("id", "draggable" + this.props.loanId);

        let draggable = svg
            .append("line")
            .attr("class", "estimation-line")
            .attr("x1", x(indexedDate))
            .attr("y1", chartHeight + 15)
            .attr("x2", x(indexedDate))
            .attr("y2", y(indexedPlanData.remainingAmount))
            .attr("stroke-dasharray", "5, 5")
            .style("fill", "none")
            .attr("marker-start", "url(#eol-dot)")
            .attr("marker-end", "url(#eol-circle)");

        let lineDraggable = draggable.node() as SVGLineElement;
        let bboxDraggable = lineDraggable.getBBox();

        bgRectDraggable
            .attr("fill", "rgba(0,0,0,0)")
            .attr("x", bboxDraggable.x - 12)
            .attr("y", bboxDraggable.y - 12)
            .attr("height", bboxDraggable.height + 24)
            .attr("width", 24);

        d3.select("#draggable" + this.props.loanId).call(drag);
        this.rootView.on("click", this.plotTap);
    };

    render() {
        return (
            <svg
                id={`payment-plan-plot-loan-${this.props.loanId}`}
                className="payment-plan-plot"
                ref={(node) => (this.node = node)}
                width={this.props.width}
                height={this.props.height}
            ></svg>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/PaymentPlanPlot/PaymentPlanPlot.tsx